import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import { tokens } from "../tokens";
import { Icon } from "./Icon";
import MuiTypography from "@mui/material/Typography";
import type { SectionListCardProps, SectionListRowMarker } from "../types";

// H2's `railList`: no inter-row gap -- the vertical rhythm comes from each
// row's own top padding, matching the timeline marker/connector geometry,
// not a list-level gap.
const LIST_RESET = { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" } as const;

// H2's own marker is a 24px circle (`04-05-GuidedExposures.dc.html`'s own
// `dot` style) centred, with no horizontal inset, inside this component's
// `markCol` (a `flex: 0 0 24px` column, `alignItems: center`). The
// connector below computes its horizontal position FROM this value, rather
// than repeating H2's own `connector.left: 19` literal, which was H2's own
// figure for a DIFFERENT box model -- H2 pairs that 19px with an 8px
// horizontal inset on its own row button (`rowBtn`'s `padding: '0 8px
// 8px'`) that this component's row grid does not carry, so reusing it here
// put the line to the marker's right rather than through its centre.
// `tokens.interactionState.targetSize.minimum` (24) is reused rather than a
// second, independent "24" literal: it is already the real-world size this
// marker circle renders at.
const MARKER_SIZE = tokens.interactionState.targetSize.minimum;
const CONNECTOR_WIDTH = 2;
/** The row's 8px inset, which the connector line also sits within. */
const ROW_INSET = tokens.spacing.xs;
const c = tokens.colour;
const hairline = `${tokens.interactionState.border.hairlineWidth}px solid ${c.neutral.borderSubtle}`;

const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

/** H2's text-link buttons on the list: the title (17px) and Reassign (15px). */
const linkButton = {
  appearance: "none",
  background: "none",
  border: "none",
  margin: 0,
  alignSelf: "flex-start",
  display: "inline-flex",
  alignItems: "center",
  textAlign: "left",
  fontFamily: "inherit",
  color: c.brand[700],
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  cursor: "pointer",
  "&:hover": { textDecorationThickness: "2px" },
  // H2 `style-focus`: the outline shows on any focus, a click included.
  "&:focus": { outline: `3px solid ${c.brand[700]}`, outlineOffset: "2px" },
} as const;

/** One stacked block of the action bar under the list. */
const actionBar = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: `${tokens.spacing.xs}px`,
  paddingTop: `${tokens.spacing.sm}px`,
  borderTop: hairline,
} as const;

function markerBackground(marker: SectionListRowMarker): { backgroundColor: string; border?: string } {
  if (marker === "complete") {
    return { backgroundColor: tokens.colour.brand[700] };
  }
  if (marker === "assigned") {
    return { backgroundColor: tokens.colour.neutral.borderSubtle };
  }
  if (marker === "active") {
    return { backgroundColor: tokens.colour.accent[500] };
  }
  return { backgroundColor: tokens.colour.neutral.white, border: `2px solid ${tokens.colour.neutral.borderControl}` };
}

function markerIcon(marker: SectionListRowMarker): ReactNode {
  if (marker === "complete") {
    return <Icon name="check" size={18} />;
  }
  if (marker === "assigned") {
    return <Icon name="person" size={18} />;
  }
  return null;
}

function markerIconColour(marker: SectionListRowMarker): string {
  // H2: Icon size="sm", tone onDark on a completed marker, default (brand 700) on an assigned one.
  return marker === "complete" ? tokens.colour.neutral.white : tokens.colour.brand[700];
}

/**
 * The H2 section-list card (ADR-20261016-d4wn, BLOCK-001 resolution):
 * H2's own white, hairline-bordered, radius-8 card (`railWrap`) around the
 * heading, meta line, per-row timeline (marker, connector, content,
 * optional assign checkbox) and the card's own footer actions.
 *
 * Built here, not composed from `Box`/`sx` in the feature file, because no
 * existing adapter primitive expresses this card's three layout needs: a
 * hairline border/divider keyed to `tokens.colour.neutral.borderSubtle`
 * (card edge, header divider, footer action dividers), conditional
 * suppression of that divider (the connector on the last row; the footer
 * divider when a preceding action already carries one), and CSS-grid
 * `justify-self` placement (the optional per-row checkbox column). `Stack`
 * and `Grid`'s own closed props (direction/gap/align/justify/wrap;
 * container/gap/columns/span) cannot express any of the three. Every
 * value here is token-only (ADR-20260914-6bk7): no literal colour outside
 * `tokens`, following `RenewalSummaryCard`'s precedent — the one literal
 * pixel value below (the header's 2px title/meta gap) mirrors a pre-
 * existing H2 pixel-fidelity figure, not a convenience shortcut.
 *
 * Deliberately screen-specific, like `RenewalSummaryCard`: it takes
 * already-computed, presentation-only row data (a closed `marker` union,
 * not the feature's own `SectionStatus` type) and fully-built `ReactNode`
 * slots for row content, the checkbox and the footer actions, so this
 * module never needs to import a feature type to render them.
 */
/**
 * ADR-20261102-h2fd (AC-21 to AC-23): H2 screen 4&5's section list. Each row: a 24px marker on a
 * connector line, 16px from the text column; the title, optional policy reference and status share
 * one left edge 2px apart; the owner's checkbox sits on the right, level with the title.
 */
export function SectionListCard({
  heading,
  progress,
  progressTestId,
  selectHeadLabel,
  notice,
  errorSummary,
  rows,
  listTestId,
  assignAction,
  continueAction,
  submitAction,
  testId,
}: Readonly<SectionListCardProps>) {
  return (
    <Box
      data-testid={testId}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${tokens.spacing.sm}px`,
        padding: { xs: `${tokens.spacing.md}px`, sm: `${tokens.spacing.lg}px` },
        backgroundColor: c.neutral.white,
        borderRadius: `${tokens.interactionState.radius.surface}px`,
        border: hairline,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", paddingBottom: `${tokens.spacing.xs}px`, borderBottom: hairline }}>
        <MuiTypography variant="h3" component="h2">
          {heading}
        </MuiTypography>
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: `${tokens.spacing.md}px` }}>
          {progress !== undefined ? (
            <Box component="span" data-testid={progressTestId} sx={{ fontSize: 14, color: c.neutral.inkMuted }}>
              {progress}
            </Box>
          ) : (
            <span />
          )}
          {selectHeadLabel !== undefined ? (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                flex: "0 0 auto",
                paddingRight: `${ROW_INSET}px`,
                textAlign: "right",
                whiteSpace: { xs: "normal", sm: "nowrap" },
                maxWidth: { xs: "5.5em", sm: "none" },
                fontSize: 15,
                fontWeight: tokens.typography.weights.bold,
                lineHeight: 1.35,
                color: c.neutral.ink,
              }}
            >
              {selectHeadLabel}
            </Box>
          ) : null}
        </Box>
      </Box>

      {notice}

      {errorSummary}

      <ol style={LIST_RESET} data-testid={listTestId}>
        {rows.map((row, index) => (
          <li key={row.key} style={{ position: "relative", display: "block" }} data-testid={row.testId}>
            {index < rows.length - 1 ? (
              <Box
                aria-hidden="true"
                sx={{
                  position: "absolute",
                  left: `${ROW_INSET + MARKER_SIZE / 2 - CONNECTOR_WIDTH / 2}px`,
                  top: "36px",
                  bottom: "-4px",
                  width: `${CONNECTOR_WIDTH}px`,
                  backgroundColor: c.neutral.timelineConnector,
                }}
              />
            ) : null}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: row.checkbox !== undefined ? "minmax(0, 1fr) 48px" : row.reassign !== undefined ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)",
                columnGap: `${tokens.spacing.xs}px`,
                alignItems: "start",
                paddingTop: `${tokens.spacing.xs}px`,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "stretch",
                  gap: `${tokens.spacing.md}px`,
                  textAlign: "left",
                  padding: `0 ${ROW_INSET}px ${ROW_INSET}px`,
                  minHeight: `${tokens.interactionState.targetSize.standard}px`,
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{ flex: `0 0 ${MARKER_SIZE}px`, display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "stretch", paddingTop: `${tokens.spacing.xxs}px` }}
                >
                  <Box
                    sx={{
                      flex: "0 0 auto",
                      width: `${MARKER_SIZE}px`,
                      height: `${MARKER_SIZE}px`,
                      borderRadius: `${tokens.interactionState.radius.pill}px`,
                      boxSizing: "border-box",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: markerIconColour(row.marker),
                      ...markerBackground(row.marker),
                    }}
                  >
                    {markerIcon(row.marker)}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: "1 1 auto", paddingBottom: `${tokens.spacing.xxs}px`, marginTop: "-6px" }}>
                  <Box
                    component="button"
                    type="button"
                    id={row.titleId}
                    onClick={row.onOpen}
                    aria-current={row.current === true ? "step" : undefined}
                    data-testid={row.openTestId}
                    sx={{
                      ...linkButton,
                      padding: "10px 0",
                      minHeight: tokens.interactionState.targetSize.standard,
                      fontSize: 17,
                      fontWeight: row.current === true ? tokens.typography.weights.bold : tokens.typography.weights.regular,
                      lineHeight: 1.35,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {row.title}
                    {row.positionText !== undefined ? (
                      <Box component="span" sx={visuallyHidden}>
                        {row.positionText}
                      </Box>
                    ) : null}
                  </Box>
                  {row.policyRef !== undefined ? (
                    <MuiTypography variant="body2" component="span" sx={{ color: c.neutral.inkMuted }}>
                      {row.policyRef}
                    </MuiTypography>
                  ) : null}
                  <MuiTypography variant="body2" component="span" sx={{ color: row.statusDone === true ? c.neutral.ink : c.neutral.inkMuted }}>
                    {row.statusLabel}
                  </MuiTypography>
                  {row.reassign !== undefined ? (
                    <Box component="span" sx={{ display: "flex", alignSelf: "flex-start" }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={row.reassign.onClick}
                        aria-expanded={row.reassign.expanded}
                        data-testid={row.reassign.testId}
                        sx={{ ...linkButton, padding: 0, minHeight: 36, fontSize: 15 }}
                      >
                        {row.reassign.label}
                        <Box component="span" sx={visuallyHidden}>
                          {` ${row.reassign.hiddenLabel}`}
                        </Box>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              </Box>
              {row.checkbox !== undefined ? (
                <Box component="span" sx={{ display: "inline-flex", alignItems: "flex-start", justifyContent: "flex-end", marginTop: "-6px" }}>
                  {row.checkbox}
                </Box>
              ) : null}
            </Box>
          </li>
        ))}
      </ol>

      {assignAction !== undefined ? <Box sx={actionBar}>{assignAction}</Box> : null}
      {continueAction !== undefined ? <Box sx={actionBar}>{continueAction}</Box> : null}
      {submitAction !== undefined ? <Box sx={actionBar}>{submitAction}</Box> : null}
    </Box>
  );
}

export type { SectionListCardRow, SectionListRowMarker } from "../types";
